// Pure adapter: API response -> profile view model. NO server-only imports.
//
// Split out of `investigadores-public.ts` (which is `server-only` because it owns
// the cached fetches) so the OWNER's client-side view can reuse the exact same
// mapping. Mirrors the split already used by `investigator-profile-cache.ts`:
// pure mapping here, server access there.

import { formatearDatoPersonal } from '@/lib/telefono';
import type { InvestigadorPublicProfileResponse } from '@siiv/schemas';
import { computeGradoFromFormaciones } from '@/lib/grado-investigador';
import { sortCvByDate } from '@/components/investigador/cv/cv-helpers';
import type { InvestigatorRecord, ProjectRecord, PublicationRecord, ResearchLineRecord } from '@/data/researchCatalog';
import type {
	CvActividadData,
	CvCapitalHumanoData,
	CvCargoData,
	CvEnlaceData,
	CvExperienciaData,
	CvFormacionData,
	CvPerfilData,
	CvPremioData,
} from '@/data/investigatorCvData';

export interface InvestigatorPublicProfileViewModel {
	/** R12 (SEO): false for `no_listado` profiles — reachable by direct link, never
	 * indexable. Lives at the view-model root, not inside `InvestigatorRecord`,
	 * because it describes the ROUTE's crawl policy, not the person. */
	indexable: boolean;
	investigator: InvestigatorRecord;
	publications: PublicationRecord[];
	projects: ProjectRecord[];
	lines: ResearchLineRecord[];
	areaSies: InvestigatorAreaSiesViewModel[];
	areaOcde: InvestigatorAreaOcdeViewModel[];
	cvPerfil: CvPerfilData;
	cvFormacion: CvFormacionData[];
	cvExperiencia: CvExperienciaData[];
	cvCapHumano: CvCapitalHumanoData[];
	cvPremios: CvPremioData[];
	cvCargos: CvCargoData[];
	cvActividades: CvActividadData[];
	cvEnlaces: CvEnlaceData[];
}

export interface InvestigatorAreaSiesViewModel {
	id: string;
	name: string;
}

export interface InvestigatorAreaOcdeViewModel {
	id: string;
	area: string;
	subarea?: string;
}

export interface InvestigatorDirectoryViewModel {
	investigators: InvestigatorRecord[];
	researchLines: ResearchLineRecord[];
}

export function makeLineSlug(idLinea: number) {
	return `linea-${idLinea}`;
}

export function makeDisplayId(prefix: string, value: string) {
	const normalized = value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');

	return `${prefix}-${normalized || 'sin-clasificar'}`;
}

export function formatBudget(value: number | null) {
	if (value === null) return '';
	return new Intl.NumberFormat('es-CL', {
		style: 'currency',
		currency: 'CLP',
		maximumFractionDigits: 0,
	}).format(value);
}

export function mapProjectStatus(project: InvestigadorPublicProfileResponse['proyectos'][number]): ProjectRecord['status'] {
	const normalized = project.estado?.toLowerCase() ?? '';

	if (normalized.includes('ejec') || project.activo === true) return 'En ejecución';
	if (normalized.includes('adjud')) return 'Adjudicado';
	if (normalized.includes('final') || project.activo === false) return 'Finalizado';
	return 'En formulación';
}

export function adaptInvestigadorPublicProfile(
	profile: InvestigadorPublicProfileResponse,
): InvestigatorPublicProfileViewModel {
	const degree = computeGradoFromFormaciones(profile.cv.formacion, profile.investigator.rolInvestigadorNombre ?? null);
	const bio = profile.perfil.biografia ?? '';
	const areaSiesMap = new Map<string, InvestigatorAreaSiesViewModel>();
	const areaOcdeMap = new Map<string, InvestigatorAreaOcdeViewModel>();

	profile.lineas.forEach((linea) => {
		const areaSies = linea.areaSies?.trim();
		if (areaSies) {
			const key = areaSies.toLocaleLowerCase('es');
			areaSiesMap.set(key, {
				id: makeDisplayId('sies', areaSies),
				name: areaSies,
			});
		}

		const areaOcde = linea.areaOcde?.trim();
		if (areaOcde) {
			// Subárea OCDE real (FK línea→subárea); no se infiere del nombre del área.
			const subarea = linea.subareaOcde?.trim() || undefined;
			const key = `${areaOcde.toLocaleLowerCase('es')}::${subarea?.toLocaleLowerCase('es') ?? ''}`;
			areaOcdeMap.set(key, {
				id: makeDisplayId('ocde', [areaOcde, subarea].filter(Boolean).join('-')),
				area: areaOcde,
				subarea,
			});
		}
	});

	const investigator: InvestigatorRecord = {
		slug: profile.slug,
		idInvestigador: profile.investigator.idInvestigador,
		displayName: profile.investigator.displayName,
		fullName: profile.investigator.fullName,
		sex: '',
		orcid: profile.investigator.orcid ?? '',
		rut: '',
		degree,
		tipoInvestigador: profile.investigator.tipoInvestigador ?? null,
		rolInvestigadorNombre: profile.investigator.rolInvestigadorNombre ?? null,
		fotoPerfilUrl: profile.investigator.fotoPerfilUrl ?? null,
		fotoPerfilOculta: profile.investigator.fotoPerfilOculta ?? false,
		nombre1: profile.investigator.nombre1 ?? null,
		nombre2: profile.investigator.nombre2 ?? null,
		apellido1: profile.investigator.apellido1 ?? null,
		apellido2: profile.investigator.apellido2 ?? null,
		formatoNombre: profile.investigator.formatoNombre ?? null,
		unit: profile.investigator.unit ?? '',
		idUnidad: profile.investigator.idUnidad ?? null,
		email: profile.investigator.emailPublic ?? '',
		telefono: profile.investigator.telefonoPublico ?? null,
		repoUrl: '',
		bio,
		researchLineSlugs: profile.lineas.map((linea) => makeLineSlug(linea.idLinea)),
		customLines: profile.lineasPersonalizadas,
		publicationSlugs: profile.publicaciones.map((publication) => `pub-${publication.id}`),
		projectSlugs: profile.proyectos.map((project) => `proyecto-${project.tipo}-${project.id}`),
		personalData: profile.datosPersonales.map((dato) => ({
			codigo: dato.codigo,
			label: dato.etiqueta,
			// El teléfono se muestra con formato chileno en TODAS partes; formatearlo
			// acá lo resuelve para el perfil, el CV, la vista del dueño y la exportación.
			value: formatearDatoPersonal(dato.codigo, dato.valor),
			span: dato.anchoColumnas,
			esPublico: dato.esPublico,
			ordenVisual: dato.ordenVisual,
			macrounidad: dato.macrounidad,
		})),
	};

	return {
		indexable: profile.indexable,
		investigator,
		publications: profile.publicaciones.map((publication): PublicationRecord => ({
			slug: `pub-${publication.id}`,
			title: publication.titulo,
			year: publication.anio,
			yearFromEarlyAccess: publication.anioEarlyAccess ?? false,
			journal: publication.journal ?? publication.tipo,
			doi: publication.doi ?? '',
			wosId: publication.wosId ?? '',
			scopusId: publication.scopusId ?? '',
			url: publication.doi ? `https://doi.org/${publication.doi}` : '',
			authorSlugs: [profile.slug],
		})),
		projects: profile.proyectos.map((project): ProjectRecord => ({
			slug: `proyecto-${project.tipo}-${project.id}`,
			name: project.titulo ?? project.codigo ?? `Proyecto ${project.id}`,
			convocatoria: project.rol ? `${project.tipo} · ${project.rol}` : project.tipo,
			status: mapProjectStatus(project),
			startDate: project.fechaInicio ?? '',
			endDate: project.fechaTermino ?? '',
			budget: formatBudget(project.montoAdjudicado),
			participantSlugs: [profile.slug],
		})),
		lines: [
			...profile.lineas.map((linea): ResearchLineRecord => ({
				slug: makeLineSlug(linea.idLinea),
				name: linea.nombreLinea ?? 'Línea sin nombre',
				unit: linea.unidad ?? '',
				description: [linea.areaSies, linea.areaOcde].filter(Boolean).join(' · '),
				unitShort: linea.unidadAbreviada,
				unitLabels: linea.unidadesEtiqueta,
				kind: 'uda',
				areaOcde: linea.areaOcde,
				subareaOcde: linea.subareaOcde,
				areaSies: linea.areaSies,
			})),
			// R66: custom lines follow the institutional ones in the order saved (no unit, no description).
			...profile.lineasPersonalizadas.map(
				(texto, indice): ResearchLineRecord => ({
					slug: `propia-${indice}`,
					name: texto,
					unit: '',
					description: '',
					kind: 'propia',
				}),
			),
		],
		areaSies: Array.from(areaSiesMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'es')),
		areaOcde: Array.from(areaOcdeMap.values()).sort((a, b) => {
			const areaCompare = a.area.localeCompare(b.area, 'es');
			if (areaCompare !== 0) return areaCompare;
			return (a.subarea ?? '').localeCompare(b.subarea ?? '', 'es');
		}),
		cvPerfil: {
			investigadorSlug: profile.slug,
			biografia: profile.perfil.biografia ?? '',
			ciudadPublica: profile.perfil.ciudadPublica ?? '',
			paisPublico: profile.perfil.paisPublico ?? '',
			idCargoMostrado: profile.perfil.idCargoMostrado ?? null,
			idFormacionMostrada: profile.perfil.idFormacionMostrada ?? null,
		},
		cvFormacion: sortCvByDate(profile.cv.formacion).map((formacion, index): CvFormacionData => ({
			id: formacion.id,
			investigadorSlug: profile.slug,
			categoria: formacion.categoria,
			tituloObtenido: formacion.tituloObtenido,
			institucion: formacion.institucion ?? '',
			ciudad: formacion.ciudad ?? undefined,
			pais: formacion.pais ?? '',
			fechaInicio: formacion.fechaInicio ?? '',
			fechaTermino: formacion.fechaTermino ?? undefined,
			fechaInicioPrecision: formacion.fechaInicioPrecision,
			fechaTerminoPrecision: formacion.fechaTerminoPrecision,
			enCurso: formacion.enCurso,
			tesisTitulo: formacion.tesisTitulo ?? undefined,
			descripcion: formacion.descripcion ?? undefined,
			destacado: index === 0,
			esPublico: formacion.esPublico,
		})),
		cvExperiencia: sortCvByDate(profile.cv.experiencia).map((experiencia, index): CvExperienciaData => ({
			id: experiencia.id,
			investigadorSlug: profile.slug,
			categoria: experiencia.categoria,
			cargo: experiencia.cargo,
			institucion: experiencia.institucion ?? '',
			unidad: experiencia.unidad ?? undefined,
			ciudad: experiencia.ciudad ?? undefined,
			pais: experiencia.pais ?? '',
			jornadaTexto: experiencia.jornadaTexto ?? undefined,
			fechaInicio: experiencia.fechaInicio ?? '',
			fechaTermino: experiencia.fechaTermino ?? undefined,
			fechaInicioPrecision: experiencia.fechaInicioPrecision,
			fechaTerminoPrecision: experiencia.fechaTerminoPrecision,
			actual: experiencia.enCurso,
			descripcion: experiencia.descripcion ?? undefined,
			destacado: index === 0,
			esPublico: experiencia.esPublico,
		})),
		cvCapHumano: profile.cv.capitalHumano.map((capitalHumano): CvCapitalHumanoData => ({
			id: capitalHumano.id,
			investigadorSlug: profile.slug,
			nombrePersona: capitalHumano.nombrePersona,
			tituloTrabajo: capitalHumano.tituloTrabajo ?? undefined,
			tipoParticipacion: capitalHumano.tipoParticipacion,
			nivelFormacion: capitalHumano.nivelFormacion,
			institucion: capitalHumano.institucion ?? '',
			unidadAcademica: capitalHumano.unidadAcademica ?? undefined,
			fechaInicio: capitalHumano.fechaInicio ?? undefined,
			fechaTermino: capitalHumano.fechaTermino ?? undefined,
			fechaInicioPrecision: capitalHumano.fechaInicioPrecision,
			fechaTerminoPrecision: capitalHumano.fechaTerminoPrecision,
			enCurso: capitalHumano.enCurso,
			anio: capitalHumano.anio ?? undefined,
			descripcion: capitalHumano.descripcion ?? undefined,
			esPublico: capitalHumano.esPublico,
		})),
		cvPremios: profile.cv.premios.map((premio, index): CvPremioData => ({
			id: premio.id,
			investigadorSlug: profile.slug,
			nombrePremio: premio.nombrePremio,
			institucionOtorgante: premio.institucionOtorgante ?? '',
			ciudad: premio.ciudad ?? undefined,
			pais: premio.pais ?? '',
			anio: premio.anio ?? undefined,
			descripcion: premio.descripcion ?? undefined,
			destacado: index === 0,
			esPublico: premio.esPublico,
		})),
		cvCargos: sortCvByDate(profile.cv.cargos).map((cargo, index): CvCargoData => ({
			id: cargo.id,
			investigadorSlug: profile.slug,
			cargoNombre: cargo.cargoNombre,
			tipoCargo: cargo.tipoCargo,
			ambitoCargo: cargo.ambitoCargo,
			entidadTexto: cargo.entidadTexto ?? undefined,
			unidad: cargo.unidad ?? undefined,
			macrounidad: cargo.macrounidad ?? undefined,
			fechaInicio: cargo.fechaInicio ?? '',
			fechaTermino: cargo.fechaTermino ?? undefined,
			fechaInicioPrecision: cargo.fechaInicioPrecision,
			fechaTerminoPrecision: cargo.fechaTerminoPrecision,
			destacado: index === 0,
			esPublico: cargo.esPublico,
		})),
		cvActividades: profile.cv.actividades.map((actividad): CvActividadData => ({
			id: actividad.id,
			investigadorSlug: profile.slug,
			categoriaActividad: actividad.categoriaActividad,
			titulo: actividad.titulo,
			institucion: actividad.institucion ?? undefined,
			ciudad: actividad.ciudad ?? undefined,
			pais: actividad.pais ?? undefined,
			fechaInicio: actividad.fechaInicio ?? undefined,
			fechaTermino: actividad.fechaTermino ?? undefined,
			fechaInicioPrecision: actividad.fechaInicioPrecision,
			fechaTerminoPrecision: actividad.fechaTerminoPrecision,
			enCurso: actividad.enCurso,
			descripcion: actividad.descripcion ?? undefined,
			esPublico: actividad.esPublico,
		})),
		cvEnlaces: profile.cv.enlaces.map((enlace): CvEnlaceData => ({
			id: enlace.id,
			investigadorSlug: profile.slug,
			tipoEnlace: enlace.tipoEnlace,
			etiqueta: enlace.etiqueta ?? undefined,
			url: enlace.url,
			esPublico: enlace.esPublico,
		})),
	};
}
